import { useSuspenseQuery } from "@tanstack/react-query"
import { useSyncExternalStore } from "react"
import { type Child, childrenQueryOptions } from "./api/children.queries"

const STORAGE_KEY = "slumra.selectedChildId"
const listeners = new Set<() => void>()

function getStoredId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function subscribe(callback: () => void) {
  listeners.add(callback)
  return () => listeners.delete(callback)
}

function emit() {
  for (const listener of listeners) listener()
}

export function selectChild(id: string) {
  try {
    localStorage.setItem(STORAGE_KEY, id)
  } catch {
    // ignore
  }
  emit()
}

export function clearSelectedChild() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
  emit()
}

// Stored child if it still exists, else the first child, else null.
function resolveSelectedChild(children: Child[], storedId: string | null): Child | null {
  return children.find((child) => child.id === storedId) ?? children[0] ?? null
}

export function getSelectedChildId(children: Child[]): string | null {
  return resolveSelectedChild(children, getStoredId())?.id ?? null
}

interface SelectedChild {
  children: Child[]
  selectedChild: Child | null
}

// Reads the prefetched children list; callers must sit under a route that ensures childrenQueryOptions.
export function useSelectedChild(): SelectedChild {
  const { data: children } = useSuspenseQuery(childrenQueryOptions)
  const storedId = useSyncExternalStore(subscribe, getStoredId, () => null)
  return { children, selectedChild: resolveSelectedChild(children, storedId) }
}
